import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadingSpinner } from '../../components/layout/LoadingSpinner';
import { ProfileImage } from '../../components/ui/ProfileImage';
import { VideoPlayer } from '../../components/admin/VideoPlayer';
import { useJobApplications, useApplicationVideo } from '../../hooks/unified/useAdminQueries';
import { unifiedApiService } from '../../services/unified';

interface ResumePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  resumeId: string;
  resumeFilename: string;
}

interface VideoIndicatorProps {
  applicationId: string;
  onVideoDetected?: (applicationId: string, hasVideo: boolean) => void;
}

function VideoIndicator({ applicationId, onVideoDetected }: VideoIndicatorProps) {
  const [showVideo, setShowVideo] = useState(false);
  const { data: videoData, isLoading } = useApplicationVideo(applicationId);

  // Notify parent when video status is determined
  React.useEffect(() => {
    if (!isLoading && onVideoDetected) {
      onVideoDetected(applicationId, !!videoData);
    }
  }, [isLoading, videoData, applicationId, onVideoDetected]);

  const handleDownloadVideo = async () => {
    try {
      const blob = await unifiedApiService.applications.downloadApplicationVideo(applicationId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = videoData?.filename || 'video.mp4';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download video:', error);
      alert('Failed to download video. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center text-sm text-gray-400">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  if (!videoData) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowVideo(true);
        }}
        className="flex items-center text-sm text-blue-600 hover:text-blue-700"
        title="View demo video"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>

      {showVideo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Demo Video</h3>
              <button
                onClick={() => setShowVideo(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <VideoPlayer
              video={videoData}
              onDownload={handleDownloadVideo}
              onError={(error) => {
                console.error('Video player error:', error);
              }}
            />
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowVideo(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResumePreviewModal({ isOpen, onClose, resumeId, resumeFilename }: ResumePreviewModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Resume Preview: {resumeFilename}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="bg-gray-100 rounded-lg p-4" style={{ height: '70vh' }}>
          <iframe
            src={`/api/resumes/${resumeId}/view`}
            className="w-full h-full border-0 rounded"
            title="Resume Preview"
          />
        </div>
        
        <div className="mt-4 flex justify-end space-x-3">
          <a
            href={`/api/resumes/${resumeId}/download`}
            download
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Download
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminJobApplicationsPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { data: applications = [], isLoading, error, refetch } = useJobApplications(jobId || '');
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'reviewed' | 'shortlisted' | 'interviewed' | 'offered' | 'hired' | 'rejected' | 'withdrawn'>('all');
  const [videoFilter, setVideoFilter] = useState<'all' | 'has_video' | 'no_video'>('all');
  const [selectedResume, setSelectedResume] = useState<{ id: string; filename: string } | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [applicationsWithVideo, setApplicationsWithVideo] = useState<Set<string>>(new Set());

  const handleVideoDetected = React.useCallback((applicationId: string, hasVideo: boolean) => {
    setApplicationsWithVideo(prev => {
      const newSet = new Set(prev);
      if (hasVideo) {
        newSet.add(applicationId);
      } else {
        newSet.delete(applicationId);
      }
      return newSet;
    });
  }, []);

  const filteredApplications = applications.filter(app => {
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    const hasVideo = applicationsWithVideo.has(app.application_id);
    const matchesVideo = 
      videoFilter === 'all' ||
      (videoFilter === 'has_video' && hasVideo) ||
      (videoFilter === 'no_video' && !hasVideo);
    
    return matchesStatus && matchesVideo;
  });

  const handleStatusUpdate = async (applicationId: string, newStatus: string) => {
    setUpdatingStatus(applicationId);
    try {
      await unifiedApiService.applications.updateApplicationStatus(applicationId, { status: newStatus });
      await refetch();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update application status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleAdvanceStage = async (applicationId: string) => {
    setUpdatingStatus(applicationId);
    try {
      await unifiedApiService.applications.advanceApplicationStage(applicationId, true);
      await refetch();
    } catch (err) {
      console.error('Failed to advance stage:', err);
      alert('Failed to advance application stage. The application may already be at the final stage.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
              <Link to="/admin/jobs" className="hover:text-primary-600">Jobs</Link>
              <span>/</span>
              <span>Applications</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Job Applications</h1>
            <p className="mt-1 text-sm text-gray-600">
              Review and manage applications for this job posting.
            </p>
          </div>
          <div className="flex items-center">
            <button className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 min-h-[44px] rounded-md text-sm font-medium transition-colors">
              Export Applications
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            Failed to load applications: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <label className="text-sm font-medium text-gray-700 hidden sm:block">Filters:</label>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 flex-1">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                className="block px-3 py-2 min-h-[44px] border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="offered">Offered</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
              <select
                value={videoFilter}
                onChange={(e) => setVideoFilter(e.target.value as typeof videoFilter)}
                className="block px-3 py-2 min-h-[44px] border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Video Status</option>
                <option value="has_video">Has Video</option>
                <option value="no_video">No Video</option>
              </select>
            </div>
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-right">
              {filteredApplications.length} of {applications.length} applications
            </div>
          </div>
        </div>

        {/* Stats - Pipeline Stages */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Applications by Stage</h3>
          <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-2 sm:gap-3">
            {[
              { key: 'total', label: 'Total', color: 'blue', count: applications.length },
              { key: 'submitted', label: 'Submitted', color: 'gray', count: applications.filter(a => a.status === 'submitted').length },
              { key: 'reviewed', label: 'Reviewed', color: 'yellow', count: applications.filter(a => a.status === 'reviewed').length },
              { key: 'shortlisted', label: 'Shortlist', color: 'indigo', count: applications.filter(a => a.status === 'shortlisted').length },
              { key: 'interviewed', label: 'Interview', color: 'purple', count: applications.filter(a => a.status === 'interviewed').length },
              { key: 'offered', label: 'Offered', color: 'cyan', count: applications.filter(a => a.status === 'offered').length },
              { key: 'hired', label: 'Hired', color: 'green', count: applications.filter(a => a.status === 'hired').length },
              { key: 'rejected', label: 'Rejected', color: 'red', count: applications.filter(a => a.status === 'rejected').length },
            ].map(({ key, label, color, count }) => (
              <div key={key} className="border border-gray-200 rounded-lg p-2 sm:p-3 text-center">
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{count}</p>
                <p className={`text-[10px] sm:text-xs font-medium text-${color}-600 truncate`}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resume
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Video
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied Date
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((application) => (
                  <tr key={application.application_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ProfileImage
                          src={undefined}
                          alt={application.candidate_name}
                          name={application.candidate_name}
                          size="sm"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{application.candidate_name}</div>
                          <div className="text-sm text-gray-500">{application.candidate_email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {application.resume_filename ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setSelectedResume({ 
                              id: application.resume_id || '', 
                              filename: application.resume_filename || '' 
                            })}
                            className="text-sm text-primary-600 hover:text-primary-900"
                          >
                            {application.resume_label || application.resume_filename}
                          </button>
                          <a
                            href={`/api/resumes/${application.resume_id}/download`}
                            download
                            className="text-gray-400 hover:text-gray-600"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No resume</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VideoIndicator 
                        applicationId={application.application_id}
                        onVideoDetected={handleVideoDetected}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
                          submitted: { label: 'Submitted', bg: 'bg-gray-100', text: 'text-gray-800' },
                          reviewed: { label: 'Reviewed', bg: 'bg-yellow-100', text: 'text-yellow-800' },
                          shortlisted: { label: 'Shortlisted', bg: 'bg-indigo-100', text: 'text-indigo-800' },
                          interviewed: { label: 'Interviewed', bg: 'bg-purple-100', text: 'text-purple-800' },
                          offered: { label: 'Offered', bg: 'bg-cyan-100', text: 'text-cyan-800' },
                          hired: { label: 'Hired', bg: 'bg-green-100', text: 'text-green-800' },
                          rejected: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-800' },
                        };
                        const config = statusConfig[application.status] || { label: application.status, bg: 'bg-gray-100', text: 'text-gray-800' };
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
                            {config.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(application.applied_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* Advance to Next Stage */}
                        {application.status !== 'hired' && application.status !== 'rejected' && (
                          <button
                            onClick={() => handleAdvanceStage(application.application_id)}
                            disabled={updatingStatus === application.application_id}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded border border-green-200 disabled:opacity-50"
                            title="Advance to Next Stage"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            Next
                          </button>
                        )}
                        {/* Reject */}
                        {application.status !== 'hired' && application.status !== 'rejected' && (
                          <button
                            onClick={() => handleStatusUpdate(application.application_id, 'rejected')}
                            disabled={updatingStatus === application.application_id}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded border border-red-200 disabled:opacity-50"
                            title="Reject Application"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Reject
                          </button>
                        )}
                        {/* View Profile */}
                        <Link
                          to={`/admin/candidates/${application.candidate_id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          View Profile
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredApplications.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No applications found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter !== 'all' 
                  ? 'Try adjusting your filter criteria.'
                  : 'No candidates have applied to this job yet.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Resume Preview Modal */}
        {selectedResume && (
          <ResumePreviewModal
            isOpen={!!selectedResume}
            onClose={() => setSelectedResume(null)}
            resumeId={selectedResume.id}
            resumeFilename={selectedResume.filename}
          />
        )}
      </div>
    </AdminLayout>
  );
}
